/* eslint-disable @typescript-eslint/no-explicit-any */
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { format, formatDistance, isBefore, isValid, parseISO } from 'date-fns/esm';
import { fr } from 'date-fns/esm/locale';
import { Constants } from '../Constants';

export class DateUtils {
    static addDays(start_date: Date, days: number) {
        const date = new Date(start_date);
        date.setDate(date.getDate() + days);
        return date;
    }
    /**
     * @param {String|Date} date - ISO format
     * @param {*} defaultValue
     * @param {Object|String} options
     * @param {Boolean} options.acceptFalsyDefault - return the 'defaultDate' even if it's falsy
     * @returns {Date} - the parsed Date, or the default date, or the current date in that order
     */
    static customParseDate = <DefaultValue = any>(
        date: string | Date,
        defaultDate: DefaultValue,
        { acceptFalsyDefault }: { acceptFalsyDefault?: boolean } = {}
    ): Date | DefaultValue => {
        if (typeof date === 'string' && isValid(parseISO(date))) return parseISO(date);

        if (date instanceof Date && isValid(date)) return date;

        if (acceptFalsyDefault) return defaultDate;

        return defaultDate || new Date();
    };

    static weekDayThreeDigits = (date: Date) => {
        const weekDay = date.toLocaleString('fr-FR', { weekday: 'long' }).substr(0, 3);
        return weekDay.charAt(0).toUpperCase() + weekDay.slice(1);
    };

    static formatDateCustom = <DefaultValue extends any>(
        rawDate: string | Date,
        customFormat: string,
        defaultValue: DefaultValue | string = '-'
    ): string | DefaultValue => {
        const parsedDate = DateUtils.customParseDate<DefaultValue | string>(rawDate, defaultValue, {
            acceptFalsyDefault: true,
        });

        if (parsedDate === defaultValue) return defaultValue;

        return parsedDate
            ? format(parsedDate as Date, customFormat, {
                  useAdditionalWeekYearTokens: true,
                  locale: fr,
              })
            : defaultValue;
    };

    /**
     * @param {String|Date} rawDate
     * @param {Date|Number} baseDate
     * @param {*} defaultValue
     * @param {Object|String} options
     * @param {Boolean} options.includeSeconds - distances less than a minute are more detailed
     * @param {Boolean} options.addSuffix - result indicates if the second date is earlier or later than the first
     */
    static formatDistanceCustom = <DefaultValue extends any>(
        rawDate: string | Date,
        baseDate: Date,
        defaultValue: DefaultValue | string | null = '-',
        { includeSeconds, addSuffix }: { includeSeconds?: boolean; addSuffix?: boolean } = {}
    ): null | string | DefaultValue => {
        const parsedDate = DateUtils.customParseDate<DefaultValue | string | null>(
            rawDate,
            defaultValue,
            {
                acceptFalsyDefault: true,
            }
        );

        if (parsedDate === defaultValue) return defaultValue;

        return parsedDate
            ? formatDistance(parsedDate as Date, baseDate, {
                  includeSeconds,
                  addSuffix,
                  locale: fr,
              })
            : defaultValue;
    };

    /**
     * @param {String|Date} rawDate
     * @param {*} defaultValue
     * @returns {String} the date formatted as 'dd/MM/yy' or the default value
     */
    static formatDateShort = <DefaultValue extends any>(
        rawDate: string | Date,
        defaultValue: DefaultValue | string = '-'
    ): string | DefaultValue =>
        DateUtils.formatDateCustom<DefaultValue>(rawDate, 'dd/MM/yy', defaultValue);

    /**
     * @param {String|Date} rawDate
     * @param {*} defaultValue
     * @returns {String} the date formatted as 'd MMMM Y' or the default value
     */
    static formatDateLong = <DefaultValue extends any>(
        rawDate: string | Date,
        defaultValue: DefaultValue | string = '-'
    ): string | DefaultValue =>
        DateUtils.formatDateCustom<DefaultValue>(rawDate, 'd MMMM Y', defaultValue);

    /**
     * @param {String|Date} rawDate
     * @param {Object|String} i18n - the i18n state or the 'at' label
     * @param {*} defaultValue
     * @returns {String}
     */
    static formatAtDateTime = <DefaultValue extends any>(
        rawDate: string | Date,
        i18n: { messages: { [x: string]: string } } | string,
        defaultValue: DefaultValue | string = '-'
    ): string | DefaultValue => {
        const label = typeof i18n === 'string' ? i18n : i18n.messages['label.at'];

        return DateUtils.formatDateCustom<DefaultValue>(
            rawDate,
            `dd/MM/yyyy '${label}' HH:mm`,
            defaultValue
        );
    };

    /**
     * @param {String|Date} rawDate
     * @param {*} defaultValue
     * @returns {String}
     */
    static formatDateTime = <DefaultValue extends any>(
        rawDate: string | Date,
        defaultValue: DefaultValue | string = '-'
    ): string | DefaultValue =>
        DateUtils.formatDateCustom<DefaultValue>(rawDate, `dd/MM/yyyy HH:mm`, defaultValue);

    /**
     * @param {String|Date} rawFirst
     * @param {String|Date} rawSecond
     * @returns {Boolean} if the first date is before the second. Will return false if any
     * of the two dates are not valid
     */

    static isDateBeforeWithParse = (rawFirst: string | Date, rawSecond: string | Date): boolean => {
        const first = DateUtils.customParseDate<null>(rawFirst, null, { acceptFalsyDefault: true });
        const second = DateUtils.customParseDate<null>(rawSecond, null, {
            acceptFalsyDefault: true,
        });

        if (!first || !second) return false;

        return isBefore(first, second);
    };

    static getDateFromMilliseconds = (milliseconds: number) => {
        var hour, minute, seconds;
        seconds = Math.floor(milliseconds / 1000);
        minute = Math.floor(seconds / 60);
        seconds = seconds % 60;
        hour = Math.floor(minute / 60);
        minute = minute % 60;
        return {
            hour: hour,
            minute: minute,
            seconds: seconds,
        };
    };

    static getDateFromMuiDate = (date?: MaterialUiPickersDate) => {
        if (date) {
            return date;
        } else {
            return undefined;
        }
    };

    static formatTwoDigitsNumber = (number?: number) => {
        if (number === undefined || number === null) {
            return undefined;
        }
        return ('0' + number).slice(-2);
    };

    static getDifferenceInDays(date1: Date, date2: Date) {
        const diff = new Date(date1).getTime() - new Date(date2).getTime();
        return Math.round(diff / (1000 * 3600 * 24));
    }

    static isDateBeforeToday = (date: Date | undefined, numberOfDaysToRemove: number = 0) => {
        if (!date) {
            return false;
        }
        const today = new Date();
        const dateToCompare = new Date(date);
        dateToCompare.setDate(dateToCompare.getDate() - numberOfDaysToRemove);
        return dateToCompare < today;
    };

    static getWeekNumber(date: Date): number {
        const yearFirstDay = new Date(date.getFullYear(), 0, 1);
        const startDayOfFirstWeek = DateUtils.getWeekStartDate(yearFirstDay);
        const lastSundayOfYear = DateUtils.getWeekStartDate(new Date(date.getFullYear(), 11, 31));
        lastSundayOfYear.setDate(lastSundayOfYear.getDate() - 1);
        const numberOfDays = Math.floor(
            (date.getTime() - startDayOfFirstWeek.getTime()) / 86400000 // (24 * 60 * 60 * 1000)
        );
        return date > lastSundayOfYear ? 1 : Math.ceil((date.getDay() + 1 + numberOfDays) / 7);
    }

    static getWeekStartDate = (date?: Date): Date => {
        if (date === undefined) {
            date = new Date();
        } else {
            date = new Date(date);
        }
        const dayOfWeek = date.getDay() || 7;
        date.setDate(date.getDate() - dayOfWeek + 1);
        date.setHours(0, 0, 0, 0);
        return date;
    };

    static getWeekEndDate = (date?: Date): Date => {
        if (date === undefined) {
            date = new Date();
        } else {
            date = new Date(date);
        }
        const dayOfWeek = date.getDay() || 7;
        date.setDate(date.getDate() + (7 - dayOfWeek));
        date.setHours(23, 59, 59, 999);
        return date;
    };

    static getWeekStartDates = (startDate?: Date, endDate?: Date) => {
        if (startDate === undefined) {
            return [];
        }
        startDate = DateUtils.getWeekStartDate(startDate);
        if (endDate === undefined) {
            endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + Constants.MAX_DAYS_OF_AFFECTATION);
        }

        const weekStartDates: Date[] = [];
        startDate.setDate(startDate.getDate() - startDate.getDay() + 1);
        let weekNumber = 0,
            date;
        do {
            date = new Date(startDate);
            date.setDate(date.getDate() + weekNumber * 7);
            weekStartDates.push(date);
            weekNumber++;
        } while (date <= endDate && weekNumber < 3);
        return weekStartDates;
    };

    static getWeekStartAndEnd = (weekString: string): { startDate: Date; endDate: Date } | null => {
        const match = weekString.match(/^(\d{4})-W(\d{2})$/);
        if (!match) {
            return null;
        }
    
        const [, year, week] = match.map(Number);
    
        // Get the first Monday of the year
        // January 4th is always in the first week of the year (ISO 8601)
        const jan4 = new Date(year, 0, 4);
        const firstMonday = new Date(jan4);
        firstMonday.setDate(jan4.getDate() - (jan4.getDay() || 7) + 1);
    
        // Calculate the first day (Monday) of the selected week
        const startDate = new Date(firstMonday);
        startDate.setDate(firstMonday.getDate() + (week - 1) * 7);
    
        // Calculate the last day (Sunday) of the selected week
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
    
        return { startDate, endDate };
    };

    static isDateBetween = (date: Date, startDate: Date, endDate: Date) => {
        return date >= startDate && date <= endDate;
    }

    static formatToDay = (date?: MaterialUiPickersDate) => {
        if (!date) {
            return '';
        }
        return format(date, 'dd');
    }

    static getPreviousWeekStartDate = (date: Date) => {
        const previousWeekStartDate = new Date(date);
        previousWeekStartDate.setDate(previousWeekStartDate.getDate() - 7);
        return previousWeekStartDate;
    }

    static getPreviousWeekEndDate = (date: Date) => {
        const previousWeekStartDate = DateUtils.getPreviousWeekStartDate(date);
        const previousWeekEndDate = new Date(previousWeekStartDate);
        previousWeekEndDate.setDate(previousWeekEndDate.getDate() + 6);
        return previousWeekEndDate;
    }
}
